export const MAX_PROSPECT_BATCH_TOUCHES = 100;

export function localTouchDate(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export const PROSPECT_CAMPAIGN = {
  startsOn: '2026-09-01',
  dueOn: '2027-09-01',
} as const;

export const PROSPECT_TOUCH_TYPES = [
  { key: 'in_person_viewing', label: 'In-person viewing', points: 5 },
  { key: 'in_person_meeting', label: 'In-person meeting', points: 3 },
  { key: 'video_coaching', label: 'Video teaching / coaching session', points: 3 },
  { key: 'check_in_call', label: 'Call to check in', points: 2 },
  { key: 'watched_shifts_online', label: 'Watched game / shifts online', points: 2 },
  { key: 'postgame_text', label: 'Text after game', points: 1 },
  { key: 'email', label: 'Email', points: 1 },
] as const;

export type ProspectTouchType = (typeof PROSPECT_TOUCH_TYPES)[number]['key'];
export type ProspectPace = 'ahead' | 'on_track' | 'behind';

export type ProspectTouchTimelineMetadata = {
  source: 'prospects-ui';
  prospectId: string;
  prospectTouchId: string;
  touchType: ProspectTouchType;
  points: number;
};

export const PROSPECT_TIER_GOALS: Readonly<Record<0 | 1 | 2 | 3, number>> = {
  0: 55,
  1: 35,
  2: 20,
  3: 10,
};

const TOUCH_POINTS = new Map<string, number>(
  PROSPECT_TOUCH_TYPES.map((touch) => [touch.key, touch.points])
);

const LEGACY_TOUCH_POINTS: Readonly<Record<string, number>> = {
  saw_play: 2,
  met: 1.5,
  online: 1.25,
  call: 1,
  text: 0.5,
};

export function prospectTouchPoints(type: string | null | undefined): number {
  if (!type) return 0;
  return TOUCH_POINTS.get(type) ?? LEGACY_TOUCH_POINTS[type] ?? 0;
}

export function prospectTouchLabel(type: string | null | undefined): string {
  return PROSPECT_TOUCH_TYPES.find((touch) => touch.key === type)?.label ?? 'Prospect touch';
}

export function prospectTouchTimelineEventId(touchId: string): string {
  return `prospect_timeline_${touchId}`;
}

export function prospectTouchTimelineTitle(type: string, points: number): string {
  return `${prospectTouchLabel(type)} (+${points})`;
}

export function prospectTouchTimelineTimestamp(dateOnly: string): number {
  return Math.floor(Date.parse(`${dateOnly}T12:00:00Z`) / 1000);
}

export function buildProspectTouchTimelineMetadata(
  input: Omit<ProspectTouchTimelineMetadata, 'source'>
): string {
  return JSON.stringify({ source: 'prospects-ui', ...input } satisfies ProspectTouchTimelineMetadata);
}

export function parseProspectTouchTimelineMetadata(
  value: string | null | undefined
): ProspectTouchTimelineMetadata | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    const validType = PROSPECT_TOUCH_TYPES.some((touch) => touch.key === parsed.touchType);
    if (
      parsed.source !== 'prospects-ui' ||
      typeof parsed.prospectId !== 'string' ||
      typeof parsed.prospectTouchId !== 'string' ||
      !validType ||
      typeof parsed.points !== 'number' ||
      !Number.isFinite(parsed.points)
    ) {
      return null;
    }
    return parsed as ProspectTouchTimelineMetadata;
  } catch {
    return null;
  }
}

export function prospectTierGoal(priority: number | null | undefined): number {
  if (priority === 0 || priority === 1 || priority === 2 || priority === 3)
    return PROSPECT_TIER_GOALS[priority];
  return PROSPECT_TIER_GOALS[3];
}

export function prospectCampaignProgress(now = new Date()): number {
  const start = Date.parse(`${PROSPECT_CAMPAIGN.startsOn}T00:00:00Z`);
  const due = Date.parse(`${PROSPECT_CAMPAIGN.dueOn}T00:00:00Z`);
  return clamp((now.getTime() - start) / (due - start), 0, 1);
}

export function prospectPace(earned: number, goal: number, now = new Date()): ProspectPace {
  const expected = goal * prospectCampaignProgress(now);
  if (earned > expected * 1.1) return 'ahead';
  if (earned < expected * 0.9) return 'behind';
  return 'on_track';
}

export function prospectProgressPercent(earned: number, goal: number): number {
  return Math.round(clamp(goal > 0 ? earned / goal : 0, 0, 1) * 100);
}

// Where today's pace puts a prospect on the way to his goal, as a percent.
export function prospectExpectedPercent(now = new Date()): number {
  return Math.round(prospectCampaignProgress(now) * 100);
}

export type ProspectProgress = {
  earned: number;
  goal: number;
  progressPercent: number;
  pace: ProspectPace;
};

// The tracker numbers for one prospect from his logged touch types.
export function prospectProgress(
  touchTypes: Array<string | null | undefined>,
  priority: number | null | undefined,
  now = new Date()
): ProspectProgress {
  const earned = touchTypes.reduce((sum, type) => sum + prospectTouchPoints(type), 0);
  const goal = prospectTierGoal(priority);
  return {
    earned,
    goal,
    progressPercent: prospectProgressPercent(earned, goal),
    pace: prospectPace(earned, goal, now),
  };
}

export function isValidDateOnly(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
